import { AudienceRequirements } from '../types/campaign';

/** Structural stats source — the Influencer type satisfies this; Firestore docs may carry either field. */
export type AudienceStatsSource = {
  follower_count?: number;
  tiktokFollowers?: number;
};

export const hasAudienceRequirements = (
  requirements?: AudienceRequirements | null
): boolean => {
  if (!requirements) return false;
  return (requirements.minFollowers ?? 0) > 0 || (requirements.maxFollowers ?? 0) > 0;
};

const maxStat = (...values: Array<number | undefined>): number =>
  Math.max(0, ...values.filter((v): v is number => typeof v === 'number' && !Number.isNaN(v)));

/**
 * Fail-closed eligibility check: missing follower stats fail any bound that is set.
 * With no platform set, bounds are best-platform-wins across IG and TikTok — including
 * maxFollowers, so a Nano range excludes a creator who is large on EITHER platform.
 * With platform set, only that platform's count is read (no stats there = fail).
 */
export const meetsAudienceRequirements = (
  influencer: AudienceStatsSource | null | undefined,
  requirements?: AudienceRequirements | null
): boolean => {
  if (!hasAudienceRequirements(requirements)) return true;
  if (!influencer) return false;
  const reqs = requirements as AudienceRequirements;

  const followers =
    reqs.platform === 'instagram' ? maxStat(influencer.follower_count)
    : reqs.platform === 'tiktok' ? maxStat(influencer.tiktokFollowers)
    : maxStat(influencer.follower_count, influencer.tiktokFollowers);
  if (followers <= 0) return false;
  if ((reqs.minFollowers ?? 0) > 0 && followers < (reqs.minFollowers as number)) return false;
  if ((reqs.maxFollowers ?? 0) > 0 && followers > (reqs.maxFollowers as number)) return false;
  return true;
};
